import { useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { EZ5050AppLayout as AppLayout } from './layouts/EZ5050AppLayout';
import { FuturesPage } from './pages/Futures';
import { LayoutPage } from './pages/Layout';
import { DashboardPage } from './pages/Dashboard';
import PositionsPage from './pages/Positions';
import HistoryPage from './pages/History';
import LeaderboardPage from './pages/Leaderboard';
import SettingsPage from './pages/Settings';
import DepositPage from './pages/Deposit';
import WithdrawPage from './pages/Withdraw';
import SupportPage from './pages/Support';
import SplashPage from './pages/Splash';
import UnauthorizedPage from './pages/Unauthorized';
import { isAuthSessionActive } from './lib/authSession';
import { appConfig } from './config/appConfig';
import { applyRouteMeta } from './lib/routeMeta';
import toast, { Toaster } from 'react-hot-toast';
import NotFound from './pages/404Page';
import SpinWheelPage from './pages/SpinAndWin';
import { WalletProvider } from './contexts/walletContext';
import Axios from './Axios';
import Maintenance from './components/Maintenance';
import Loader from './components/loader';
import { socket } from './config/socket.io';
import consts from './constant';

function PositionLayout({ children }) {
  return <div className="terminal-body terminal-body--positions">{children}</div>;
}

function RequireAuth({ children }) {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const code = searchParams.get('code');
  if (code) {
    return <Navigate to={`/authenticating${location.search}`} replace state={{ from: location.pathname }} />;
  }
  if (!isAuthSessionActive()) {
    return <Navigate to="/authenticating" replace state={{ from: location.pathname }} />;
  }
  return children;
}

let timer;


function AppContent() {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [isMaintenance, setIsMaintenance] = useState(false);
  const [pageMaintenance, setPageMaintenance] = useState({});
  const token = localStorage.getItem("olympusMons");

  const routeToKeyMap = useMemo(() => ({
    "/dashboard": "dashboard",
    "/futures/up-down": "trade",
    "/futures/high-low": "trade",
    "/futures": "trade",
    "/history": "history"
  }), []);

  const checkMaintenance = async () => {
    try {
      const { data } = await Axios.get("/settings/getSystemSettings");
      if (data.success) {
        setIsMaintenance(data.result.trade_maintenance);
        setPageMaintenance(data.result.trade_pages || {});
      }
    } catch (error) {
      console.error("Error checking maintenance status:", error);
    }
  };

  useEffect(() => {
    checkMaintenance();
  }, [location.pathname]);

  useEffect(() => {
    const handleRegister = () => {
      const currentToken = localStorage.getItem("olympusMons");
      if (currentToken) {
        socket.emit("register_user", { token: currentToken });
      }
    };

    socket.connect();
    socket.on("connect", handleRegister);
    socket.on("reconnect", handleRegister);

    if (socket.connected) {
      handleRegister();
    }

    let timer;
    socket.on("user_blocked", ({ userId }) => {
      toast.error("Your account has been blocked. Please contact support.");
      timer = setTimeout(() => {
        localStorage.clear();
        window.location.href = `${consts.CRMUrl}/login`;
      }, 2000);
    });

    return () => {
      socket.off("connect", handleRegister);
      socket.off("reconnect", handleRegister);
      socket.off("user_blocked");
      if (timer) clearTimeout(timer);
    };
  }, [location.pathname]);

  useEffect(() => {
    setLoading(true);

    const loadingTimer = setTimeout(() => {
      setLoading(false);
    }, 400);

    return () => {
      clearTimeout(loadingTimer);
    };
  }, [location.pathname]);

  const currentPathKey = Object.keys(routeToKeyMap).find(path => location.pathname.startsWith(path));
  const activeKey = location.pathname === "/" ? "trade" : (currentPathKey ? routeToKeyMap[currentPathKey] : null);
  const isPageUnderMaintenance = Boolean(activeKey && pageMaintenance[activeKey]);
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    applyRouteMeta({
      pathname: location.pathname,
      origin: window.location.origin,
      projectName: appConfig.projectName
    });
  }, [location.pathname]);

  const logout = () => {
    try {
      if (localStorage.getItem("olympusMons")) {
        if (timer) {
          clearTimeout(timer);
        }
        timer = setTimeout(async () => {

          window.localStorage.clear();
          // navigate("/401");
          window.location.href = `${consts.CRMUrl}/login`;

        }, 60000 * 10);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    checkMaintenance();
    logout();

    // Activity listeners
    window.addEventListener("mousemove", logout);
    window.addEventListener("keypress", logout);
    window.addEventListener("click", logout);

    return () => {
      window.removeEventListener("mousemove", logout);
      window.removeEventListener("keypress", logout);
      window.removeEventListener("click", logout);
      if (timer) clearTimeout(timer);
    };
  }, [location.pathname]);

  return (
    <WalletProvider>
      <Loader isLoading={loading} />
      {isMaintenance ? (
        <Maintenance />
      ) : (
        <Routes>
          <Route path="/login" element={<Navigate to="/authenticating" replace />} />
          <Route path="/register" element={<Navigate to="/authenticating" replace />} />
          <Route path="/forgot-password" element={<Navigate to="/authenticating" replace />} />
          <Route path="/verify-email" element={<Navigate to="/authenticating" replace />} />
          <Route path="/resend-verification" element={<Navigate to="/authenticating" replace />} />
          <Route path="/reset-password" element={<Navigate to="/authenticating" replace />} />
          <Route path="/authenticating" element={<SplashPage />} />
          <Route path="/401" element={<UnauthorizedPage />} />
          <Route
            element={
              <RequireAuth>
                {isPageUnderMaintenance ? (
                  <AppLayout hideOutlet={true}>
                    <Maintenance hideOverlay={true} />
                  </AppLayout>
                ) : (
                  <AppLayout />
                )}
              </RequireAuth>
            }
          >
            {/* Primary Home Page is the Up/Down Trading Platform */}
            <Route path="/" element={<LayoutPage />} />
            
            {/* Replace /futures/up-down slug with / */}
            <Route path="/futures/up-down" element={<Navigate to="/" replace />} />
            <Route path="/futures/:mode" element={<LayoutPage />} />
            
            <Route
              path="/positions"
              element={
                <PositionsPage />
              }
            />
            <Route
              path="/history"
              element={
                <HistoryPage />
              }
            />
            <Route
              path="/leaderboard"
              element={
                <LeaderboardPage />
              }
            />
            <Route
              path="/settings"
              element={
                <SettingsPage />
              }
            />
            <Route
              path="/deposit"
              element={
                <DepositPage />
              }
            />
            <Route
              path="/withdraw"
              element={
                <WithdrawPage />
              }
            />
            <Route
              path="/support"
              element={
                <SupportPage />
              }
            />
            <Route
              path="/dashboard"
              element={
                <DashboardPage />
              }
            />
            <Route
              path="/spin-wheel"
              element={
                <SpinWheelPage />
              }
            />

            <Route path="/404page" element={<NotFound />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      )}
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 3200,
          style: {
            background: 'linear-gradient(135deg, rgba(18, 24, 34, 0.96) 0%, rgba(13, 17, 24, 0.98) 100%)',
            color: '#f8fafc',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: '12px',
            boxShadow: '0 12px 36px rgba(0, 0, 0, 0.65), 0 0 16px rgba(0, 192, 118, 0.1)',
            padding: '10px 16px',
            fontSize: '12.5px',
            fontWeight: '500',
          },
          success: {
            iconTheme: {
              primary: '#00c076',
              secondary: '#121721',
            },
            style: {
              border: '1px solid rgba(0, 192, 118, 0.35)',
              boxShadow: '0 12px 36px rgba(0, 0, 0, 0.65), 0 0 16px rgba(0, 192, 118, 0.2)',
            },
          },
          error: {
            iconTheme: {
              primary: '#f6465d',
              secondary: '#121721',
            },
            style: {
              border: '1px solid rgba(246, 70, 93, 0.35)',
              boxShadow: '0 12px 36px rgba(0, 0, 0, 0.65), 0 0 16px rgba(246, 70, 93, 0.2)',
            },
          },
        }}
      />
    </WalletProvider>

  );
}

export default function App() {
  return <AppContent />;
}
